import { detectNotation, parseChord } from '../chords/chord'
import { parseKey } from '../chords/key'
import type { Notation } from '../chords/notes'
import type {
  ChordAnchor,
  Line,
  Occurrence,
  Provenance,
  RevisionContent,
  Section,
  SectionKind,
} from '../schema/v1'
import { normalizeForSearch } from '../search'
import { normalizeSource } from '../text'
import type { Issue } from '../validate'

/**
 * Importa texto con los acordes en la línea de encima de la letra, que es como se pegan las
 * transcripciones de las webs de acordes (§4, «material tuyo»):
 *
 *     Am          G
 *     Cuento los faroles del muelle
 *
 * Cada acorde se ancla al carácter de la letra que tiene debajo (columna → posición en el texto
 * NFC). No depende de los espacios de alineación: una vez importado, el anclaje es la posición.
 *
 * Reconoce: etiquetas de sección («Estrofa 1:», «[Estribillo]», «(Coro final)», «Intro: Am G C»)
 * y partes al principio de la letra («A) …»), repeticiones de una sección ya vista, líneas solo de
 * acordes (introducciones, instrumentales, compases entre barras), acordes unidos por un guion,
 * marcas «x2», tono, cejilla, tempo, compás y afinación en la cabecera, y bloques de tablatura. Un
 * bloque en blanco cierra la sección. Sin nombres, el estribillo es el bloque cuya letra se repite.
 */
export interface ChordsOverLyricsOptions {
  /** Notación de los acordes. Por defecto se detecta. */
  notation?: Notation | 'auto'
}

export interface ChordsOverLyricsResult {
  content: RevisionContent
  issues: Issue[]
}

const KEYWORD =
  '(pre[- ]?estribillo|pre[- ]?coro|introducci[oó]n|intro|estrofa|verso|estribillo|coro|puente|solo|instrumental|interludio|final|outro|coda|riff|rasgueo|arpegio|parte|tablatura|tab)'
const REPEAT = '(?:[\\[(]?\\s*[x×]\\s?\\d+\\s*[\\])]?|bis)'
const LABEL = new RegExp(
  `^\\s*[\\[(]?\\s*${KEYWORD}(?:\\s+(?:\\d+|[ivx]+|[a-h]))?(?:\\s*[-–]\\s*[\\wáéíóúüñ ]{1,20})?\\s*${REPEAT}?\\s*[\\])]?\\s*(?::\\s*(.*))?$`,
  'i',
)
const BRACKETED = /^\s*[[(]\s*([^\])]{1,60}?)\s*[\])]\s*:?\s*$/
/** «(Coro final)», «[Solo de guitarra]»: entre paréntesis, una palabra de sección y algo más. */
const BRACKETED_LABEL = new RegExp(`^${KEYWORD}(?![\\wáéíóúüñ])`, 'i')
/** «Coro final:»: una palabra de sección, unas pocas más y los dos puntos al final. */
const FREE_LABEL = new RegExp(`^\\s*${KEYWORD}(?![\\wáéíóúüñ])[\\wáéíóúüñ ]{0,24}:\\s*$`, 'i')
/** «A) Quizás porque…»: la parte de la canción marcada al principio de la letra. */
const PART_PREFIX = /^([A-H1-9])\)\s+/

const KIND_BY_KEYWORD: Record<string, SectionKind> = {
  intro: 'intro',
  introduccion: 'intro',
  estrofa: 'verse',
  verso: 'verse',
  estribillo: 'chorus',
  coro: 'chorus',
  preestribillo: 'prechorus',
  precoro: 'prechorus',
  puente: 'bridge',
  solo: 'solo',
  instrumental: 'instrumental',
  interludio: 'interlude',
  final: 'outro',
  outro: 'outro',
  coda: 'outro',
  tab: 'tab',
  tablatura: 'tab',
}

const REPEAT_TOKEN = /^\(?[x×]\s?\d+\)?$/i
const SEPARATOR_TOKEN = /^(?:[|:/\\%~·•–—-]+|\.{2,}|\|:|:\|)$/
const TAB_LINE = /^\s*[eEBGDAa]\s*[|:]?[-\d|hpbrxs/\\~^()<>.\s]*-{3,}/

const CAPO_LINE =
  /^\s*(?:cejilla|capo)\s*(?:en|:|=)?\s*(?:el\s+)?(?:traste\s+)?(\d{1,2})\s*(?:[ªºa°]|\s*traste)?\s*\.?\s*$/i
const KEY_LINE = /^\s*(?:tono|tonalidad|key)\s*[:=]\s*(\S+)\s*$/i
const TEMPO_LINE = /^\s*(?:tempo|bpm)\s*[:=]?\s*(\d{2,3})\s*(?:bpm)?\s*$/i
const TIME_LINE = /^\s*(?:comp[aá]s|time)\s*[:=]?\s*(\d{1,2}\/\d{1,2})\s*$/i
const TUNING_LINE = /^\s*(?:afinaci[oó]n|tuning)\s*[:=]\s*(.{1,40}?)\s*$/i

const IMPORT_PROVENANCE: Provenance = { kind: 'import', method: 'chords-over-lyrics' }

interface Token {
  text: string
  col: number
}

type Classified =
  | { kind: 'chord'; symbol: string; col: number }
  | { kind: 'repeat'; text: string }
  | { kind: 'separator' }
  | { kind: 'word' }

const ALPHA_RUN = /^(?:maj|Maj|MAJ|min|dim|aug|aum|sus|add|omit|alt|m|M|b|ø|Δ)+$/

/**
 * ¿Es un acorde de verdad y no una palabra que lo parece? El parser de acordes acepta «Solo»
 * (Sol disminuido) y «Mimo» (Mi menor con una «o»), así que aquí se exige además que las letras
 * del sufijo sean sufijos conocidos. «o» y «no» solo valen seguidos de un número (Co7, Cno3).
 */
export function looksLikeChord(token: string): boolean {
  const first = token[0]
  if (!first || first === first.toLowerCase()) return false
  const parsed = parseChord(token)
  if (!parsed) return false
  if (parsed.kind === 'nc') return true
  const suffix = parsed.suffix.replace(/(?:omit|no|o)(?=\d)/g, '')
  const runs = suffix.match(/[A-Za-zøΔ]+/g) ?? []
  return runs.every((run) => ALPHA_RUN.test(run))
}

function tokenize(line: string): Token[] {
  return [...line.matchAll(/\S+/g)].map((match) => ({ text: match[0], col: match.index ?? 0 }))
}

function classify(token: Token): Classified {
  if (SEPARATOR_TOKEN.test(token.text)) return { kind: 'separator' }
  if (REPEAT_TOKEN.test(token.text))
    return { kind: 'repeat', text: token.text.replace(/[()]/g, '') }
  const wrapped = /^([([{*|]*)(.*?)([)\]}*,;:|]*)$/.exec(token.text)
  const inner = wrapped?.[2] ?? token.text
  if (inner !== '' && looksLikeChord(inner)) {
    return { kind: 'chord', symbol: inner, col: token.col + (wrapped?.[1]?.length ?? 0) }
  }
  return { kind: 'word' }
}

interface ChordLine {
  chords: { symbol: string; col: number }[]
  marks: string[]
}

/**
 * «A#79-A7b9»: dos acordes en el mismo hueco unidos por un guion. Se separan solo si todas las
 * partes son acordes; «Am7-5» sigue siendo un acorde.
 */
function splitJoined(token: Token): Token[] {
  const parts = token.text.split('-')
  if (parts.length < 2 || parts.some((part) => part === '')) return [token]
  const tokens: Token[] = []
  let col = token.col
  for (const part of parts) {
    tokens.push({ text: part, col })
    col += part.length + 1
  }
  return tokens.every((item) => classify(item).kind === 'chord') ? tokens : [token]
}

/** Una línea es de acordes si todo lo que contiene son acordes, separadores o marcas «x2». */
function asChordLine(text: string): ChordLine | null {
  const classified = tokenize(text).flatMap(splitJoined).map(classify)
  const chords = classified.flatMap((item) => (item.kind === 'chord' ? [item] : []))
  if (chords.length === 0 || classified.some((item) => item.kind === 'word')) return null
  return {
    chords: chords.map((item) => ({ symbol: item.symbol, col: item.col })),
    marks: classified.flatMap((item) => (item.kind === 'repeat' ? [item.text] : [])),
  }
}

/** Cuántos acordes tiene la línea si es una línea solo de acordes (0 si no lo es). */
export function chordLineSize(text: string): number {
  return asChordLine(expandTabs(text))?.chords.length ?? 0
}

function isRepeatOnly(text: string): boolean {
  const classified = tokenize(text).map(classify)
  return (
    classified.length > 0 &&
    classified.some((item) => item.kind === 'repeat') &&
    classified.every((item) => item.kind === 'repeat' || item.kind === 'separator')
  )
}

function expandTabs(line: string): string {
  let output = ''
  for (const char of line) {
    output += char === '\t' ? ' '.repeat(4 - (output.length % 4)) : char
  }
  return output
}

function kindOfLabel(label: string): SectionKind {
  const key =
    normalizeForSearch(label)
      .replace(/^\W*pre[\s-]+/, 'pre')
      .match(/[a-z]+/)?.[0] ?? ''
  return KIND_BY_KEYWORD[key] ?? 'other'
}

/** Clave para saber si dos etiquetas son la misma sección: sin número de repetición ni «x2». */
function labelKey(label: string): string {
  return normalizeForSearch(label)
    .replace(/[[\]()]/g, ' ')
    .replace(/\b(?:x|×)\s?\d+\b|\bbis\b/g, ' ')
    .replace(/[\s:.-]+/g, ' ')
    .trim()
}

function cleanLabel(text: string): string {
  return text
    .replace(/^[\s[(]+/, '')
    .replace(/[\s:\])]+$/, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Muchas transcripciones no ponen nombre a las secciones. Si la canción no tiene un estribillo con
 * nombre, el bloque sin nombre cuya letra se repite más veces (al menos dos) pasa a ser el
 * estribillo. La letra se compara sin tildes, mayúsculas ni signos, y sin mirar los acordes. Con
 * un empate gana el que aparece antes.
 */
function markRepeatedChorus(sections: Section[]): void {
  if (sections.some((section) => section.kind === 'chorus')) return
  const groups = new Map<string, Section[]>()
  for (const section of sections) {
    if (section.kind !== 'other' || section.label) continue
    const lyrics = section.lines
      .filter((line) => line.kind !== 'comment' && /\p{L}/u.test(line.text))
      .map((line) =>
        normalizeForSearch(line.text)
          .replace(/[^\p{L}\p{N}]+/gu, ' ')
          .trim(),
      )
      .join('\n')
    if (lyrics === '') continue
    groups.set(lyrics, [...(groups.get(lyrics) ?? []), section])
  }
  let chorus: Section[] = []
  for (const group of groups.values()) {
    if (group.length > chorus.length) chorus = group
  }
  if (chorus.length < 2) return
  for (const section of chorus) section.kind = 'chorus'
}

export function parseChordsOverLyrics(
  source: string,
  options: ChordsOverLyricsOptions = {},
): ChordsOverLyricsResult {
  const issues: Issue[] = []
  const warn = (code: string, lineNumber: number, message: string) =>
    issues.push({ severity: 'warning', code, path: `line:${lineNumber}`, message })

  const counters = { s: 0, l: 0, c: 0, o: 0 }
  const nextId = (prefix: keyof typeof counters) => `${prefix}${++counters[prefix]}`

  const sections: Section[] = []
  const sequence: Occurrence[] = []
  const labelKeys = new Map<string, string>()
  const provenance: Record<string, Provenance> = {}
  const allChords: string[] = []
  let capo = 0
  let shapeKey: string | undefined
  let timeSignature: string | undefined
  let tuning: string | undefined
  let bpm: number | undefined

  let current: Section | null = null
  let pending: (ChordLine & { lineNumber: number }) | null = null

  const openSection = (kind: SectionKind, label?: string) => {
    const section: Section = { id: nextId('s'), kind, lines: [] }
    if (label) {
      section.label = label
      labelKeys.set(section.id, labelKey(label))
    }
    sections.push(section)
    sequence.push({ id: nextId('o'), section: section.id })
    current = section
    return section
  }

  /** Cierra la sección. Una etiqueta sin contenido que repite una ya vista es una repetición. */
  const closeSection = (lineNumber: number) => {
    const section = current
    current = null
    if (!section || section.lines.length > 0) return
    const key = labelKeys.get(section.id)
    const earlier =
      key === undefined
        ? undefined
        : sections.find(
            (item) => item !== section && item.lines.length > 0 && labelKeys.get(item.id) === key,
          )
    if (earlier) {
      sections.splice(sections.indexOf(section), 1)
      const index = sequence.findIndex((occurrence) => occurrence.section === section.id)
      sequence[index] = { id: sequence[index]!.id, section: earlier.id }
    } else {
      warn('import.empty-section', lineNumber, `Sección sin contenido: ${section.label ?? ''}`)
    }
  }

  const addLine = (line: Line) => {
    if (!current) openSection('other')
    current!.lines.push(line)
  }

  const comment = (text: string) => addLine({ id: nextId('l'), kind: 'comment', text, chords: [] })

  const addChordsOnly = (found: ChordLine) => {
    const chords: ChordAnchor[] = found.chords.map((chord, index) => ({
      id: nextId('c'),
      at: index,
      chord: chord.symbol,
    }))
    found.chords.forEach((chord) => allChords.push(chord.symbol))
    addLine({ id: nextId('l'), text: ' '.repeat(Math.max(0, chords.length - 1)), chords })
    found.marks.forEach(comment)
  }

  const flushPending = () => {
    if (!pending) return
    addChordsOnly(pending)
    pending = null
  }

  const rawLines = normalizeSource(source).split('\n').map(expandTabs)

  rawLines.forEach((raw, index) => {
    const lineNumber = index + 1
    const text = raw.replace(/\s+$/, '')

    if (text.trim() === '') {
      flushPending()
      closeSection(lineNumber)
      return
    }

    if (TAB_LINE.test(text)) {
      flushPending()
      if (current?.kind !== 'tab') {
        if (current && current.lines.length === 0) {
          // «Intro:» seguido de una tablatura: la etiqueta se queda y la sección pasa a ser tab.
          current.kind = 'tab'
        } else {
          closeSection(lineNumber)
          openSection('tab')
        }
        warn('import.tab-block', lineNumber, 'Bloque de tablatura: revisa que se vea bien')
      }
      addLine({ id: nextId('l'), text: text.trimEnd(), chords: [] })
      return
    }

    const capoMatch = CAPO_LINE.exec(text)
    const keyMatch = KEY_LINE.exec(text)
    const tempoMatch = TEMPO_LINE.exec(text)
    const timeMatch = TIME_LINE.exec(text)
    const tuningMatch = TUNING_LINE.exec(text)
    if (capoMatch || keyMatch || tempoMatch || timeMatch || tuningMatch) {
      flushPending()
      if (capoMatch) {
        const value = Number.parseInt(capoMatch[1]!, 10)
        if (value >= 0 && value <= 12) {
          capo = value
          provenance.capo = IMPORT_PROVENANCE
        } else warn('import.bad-capo', lineNumber, `Cejilla no válida: ${capoMatch[1]}`)
      } else if (keyMatch) {
        if (parseKey(keyMatch[1]!)) {
          shapeKey = keyMatch[1]!
          provenance.shapeKey = IMPORT_PROVENANCE
        } else warn('import.bad-key', lineNumber, `Tono no reconocido: ${keyMatch[1]}`)
      } else if (tempoMatch) {
        const value = Number.parseInt(tempoMatch[1]!, 10)
        if (value >= 20 && value <= 320) {
          bpm = value
          provenance['tempo.reference'] = IMPORT_PROVENANCE
        } else warn('import.bad-tempo', lineNumber, `Tempo no válido: ${tempoMatch[1]}`)
      } else if (timeMatch) {
        if (/^([1-9]|1[0-6])\/(1|2|4|8|16)$/.test(timeMatch[1]!)) {
          timeSignature = timeMatch[1]!
          provenance.timeSignature = IMPORT_PROVENANCE
        } else warn('import.bad-time', lineNumber, `Compás no válido: ${timeMatch[1]}`)
      } else if (tuningMatch) {
        tuning = tuningMatch[1]!
      }
      return
    }

    // Antes que la etiqueta: «[Am]» o «Intro» no pueden confundirse con una línea de acordes.
    const chordLine = asChordLine(text)
    if (chordLine) {
      flushPending()
      pending = { ...chordLine, lineNumber }
      return
    }

    const label = LABEL.exec(text)
    if (label) {
      flushPending()
      closeSection(lineNumber)
      const head = text.slice(0, label[0].length - (label[2]?.length ?? 0))
      const name = cleanLabel(head)
      openSection(kindOfLabel(name), name)
      const rest = label[2]?.trim()
      if (rest) {
        const found = asChordLine(rest)
        if (found) addChordsOnly(found)
        else addLine({ id: nextId('l'), text: rest, chords: [] })
      }
      return
    }

    if (FREE_LABEL.test(text)) {
      flushPending()
      closeSection(lineNumber)
      const name = cleanLabel(text)
      openSection(kindOfLabel(name), name)
      return
    }

    if (isRepeatOnly(text)) {
      flushPending()
      comment(text.trim().replace(/[()]/g, ''))
      return
    }

    const bracketed = BRACKETED.exec(text)
    if (bracketed) {
      flushPending()
      const name = cleanLabel(bracketed[1]!)
      if (BRACKETED_LABEL.test(name)) {
        closeSection(lineNumber)
        openSection(kindOfLabel(name), name)
      } else {
        comment(bracketed[1]!)
      }
      return
    }

    // Línea de letra: se queda con los acordes de encima, si los hay.
    let lyric = text.trimStart()
    let shift = text.length - lyric.length
    const part = PART_PREFIX.exec(lyric)
    if (part) {
      // «A)» nombra la sección que empieza aquí («Parte A»); los acordes siguen sobre su sílaba.
      const name = `Parte ${part[1]!}`
      if (!current) openSection('other', name)
      else if (!current.label) {
        current.label = name
        labelKeys.set(current.id, labelKey(name))
      }
      lyric = lyric.slice(part[0].length)
      shift += part[0].length
    }
    const anchors: ChordAnchor[] = []
    const above: (ChordLine & { lineNumber: number }) | null = pending
    pending = null
    if (above) {
      for (const chord of above.chords) {
        anchors.push({ id: nextId('c'), at: Math.max(0, chord.col - shift), chord: chord.symbol })
        allChords.push(chord.symbol)
      }
      // Compases que siguen después de la letra: cada acorde se queda en su columna.
      const last = anchors[anchors.length - 1]?.at ?? 0
      if (last > lyric.length) lyric = lyric.padEnd(last)
    }
    addLine({ id: nextId('l'), text: lyric, chords: anchors })
    above?.marks.forEach(comment)
  })

  flushPending()
  closeSection(rawLines.length)
  markRepeatedChorus(sections)

  const notation =
    options.notation && options.notation !== 'auto' ? options.notation : detectNotation(allChords)

  const content: RevisionContent = {
    notation,
    capo,
    tempo: bpm === undefined ? {} : { reference: { bpm } },
    sections,
    sequence,
    beatMap: { status: 'absent', events: [] },
    provenance,
  }
  if (shapeKey) content.shapeKey = shapeKey
  if (timeSignature) content.timeSignature = timeSignature
  if (tuning) content.tuning = tuning
  return { content, issues }
}
